import { useState, useEffect, useCallback, useRef } from 'react'

// Global cache for blob URLs to prevent unnecessary recreation
const blobUrlCache = new Map()
const MAX_CACHE_SIZE = 100 // Limit cache size to prevent memory issues

/**
 * Hook for managing blob URLs from API responses with caching and stability
 * 
 * @param {Function} fetchFunction - Function that returns a promise resolving to blob data
 * @param {Array} dependencies - Dependencies for the fetch function
 * @param {string} type - MIME type for the blob (optional)
 * @returns {Object} - { blobUrl, isLoading, error, refetch }
 */
export const useBlobUrlFromApi = (fetchFunction, dependencies = [], type = null) => {
  const [blobUrl, setBlobUrl] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const currentBlobUrlRef = useRef(null)
  const fetchPromiseRef = useRef(null)
  const cacheKeyRef = useRef(null)

  const revokeBlobUrl = useCallback((url) => {
    if (url && typeof url === 'string' && url.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(url)
      } catch (error) {
        console.error('Failed to revoke blob URL:', error)
      }
    }
  }, [])

  const createBlobUrl = useCallback((blobData, mimeType) => {
    if (!blobData) return null
    
    try {
      // If it's already a File or Blob, use it directly
      if (blobData instanceof File || blobData instanceof Blob) {
        return URL.createObjectURL(blobData)
      }

      // If it's raw data, create a Blob first
      const blob = new Blob([blobData], { type: mimeType || 'application/octet-stream' })
      return URL.createObjectURL(blob)
    } catch (error) {
      console.error('Failed to create blob URL:', error)
      return null
    }
  }, [])

  // Create a stable cache key from dependencies
  const createCacheKey = useCallback((deps) => {
    return deps.map(dep => {
      if (dep === null || dep === undefined) return 'null'
      if (typeof dep === 'object') return JSON.stringify(dep)
      return String(dep)
    }).join('|')
  }, [])

  // Store fetchFunction in a ref so we always use the latest version
  const fetchFunctionRef = useRef(fetchFunction)
  useEffect(() => {
    fetchFunctionRef.current = fetchFunction
  }, [fetchFunction])

  const fetchBlob = useCallback(async () => {
    const currentFetchFunction = fetchFunctionRef.current
    if (!currentFetchFunction) return

    // Create cache key
    const cacheKey = createCacheKey(dependencies)
    cacheKeyRef.current = cacheKey

    // Check if we already have a blob URL for this cache key
    if (blobUrlCache.has(cacheKey)) {
      const cachedUrl = blobUrlCache.get(cacheKey)
      setBlobUrl(cachedUrl)
      setIsLoading(false)
      setError(null)
      currentBlobUrlRef.current = cachedUrl
      return
    }

    // Prevent multiple simultaneous fetches for the same data
    if (fetchPromiseRef.current) {
      try {
        const result = await fetchPromiseRef.current
        if (cacheKeyRef.current === cacheKey) {
          setBlobUrl(result)
          currentBlobUrlRef.current = result
        }
        return
      } catch (err) {
        // If the previous fetch failed, continue with new fetch
      }
    }

    setIsLoading(true)
    setError(null)

    const fetchPromise = (async () => {
      try {
        const blobData = await currentFetchFunction()
        
        // Check if we're still the current fetch (component hasn't unmounted or dependencies changed)
        if (cacheKeyRef.current !== cacheKey) {
          return null
        }

        if (blobData) {
          const newBlobUrl = createBlobUrl(blobData, type)
          if (newBlobUrl) {
            // Cache the blob URL with size limit
            if (blobUrlCache.size >= MAX_CACHE_SIZE) {
              // Remove oldest entry (first in Map)
              const firstKey = blobUrlCache.keys().next().value
              const oldUrl = blobUrlCache.get(firstKey)
              if (oldUrl) {
                revokeBlobUrl(oldUrl)
              }
              blobUrlCache.delete(firstKey)
            }
            blobUrlCache.set(cacheKey, newBlobUrl)
            return newBlobUrl
          }
        }
        return null
      } catch (err) {
        console.error('Failed to fetch blob:', err)
        // Re-throw the error so it can be caught by the outer try-catch
        throw err
      }
    })()

    fetchPromiseRef.current = fetchPromise

    try {
      const result = await fetchPromise
      if (cacheKeyRef.current === cacheKey) {
        if (result) {
          setBlobUrl(result)
          currentBlobUrlRef.current = result
        } else {
          // No image available - set to null and stop loading
          setBlobUrl(null)
          currentBlobUrlRef.current = null
        }
      }
    } catch (err) {
      if (cacheKeyRef.current === cacheKey) {
        setError(err)
        setBlobUrl(null)
        currentBlobUrlRef.current = null
      }
    } finally {
      if (cacheKeyRef.current === cacheKey) {
        setIsLoading(false)
      }
      fetchPromiseRef.current = null
    }
  }, [dependencies, createBlobUrl, type, createCacheKey, revokeBlobUrl])

  useEffect(() => {
    fetchBlob()

    // Cleanup function
    return () => {
      // Don't revoke blob URL here as it might be cached and used by other components
      fetchPromiseRef.current = null
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependencies, type])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Only revoke if this component was the last to use this blob URL
      if (currentBlobUrlRef.current && cacheKeyRef.current) {
        const cacheKey = cacheKeyRef.current
        const cachedUrl = blobUrlCache.get(cacheKey)
        
        // If the cached URL is the same as our current URL, remove from cache and revoke
        if (cachedUrl === currentBlobUrlRef.current) {
          blobUrlCache.delete(cacheKey)
          revokeBlobUrl(currentBlobUrlRef.current)
        }
        // If the cached URL is different, it means another component is using it, so don't revoke
      }
      
      fetchPromiseRef.current = null
    }
  }, [revokeBlobUrl])

  const refetch = useCallback(async () => {
    const currentFetchFunction = fetchFunctionRef.current
    if (!currentFetchFunction) return

    // Create cache key
    const cacheKey = createCacheKey(dependencies)
    cacheKeyRef.current = cacheKey

    // For refetch, we want to bypass the cache and always fetch fresh data
    setIsLoading(true)
    setError(null)

    try {
      const blobData = await currentFetchFunction()
      
      // Check if we're still the current fetch (component hasn't unmounted or dependencies changed)
      if (cacheKeyRef.current !== cacheKey) {
        return
      }

      if (blobData) {
        const newBlobUrl = createBlobUrl(blobData, type)
        if (newBlobUrl) {
          // Update cache with new blob URL
          if (blobUrlCache.size >= MAX_CACHE_SIZE) {
            // Remove oldest entry (first in Map)
            const firstKey = blobUrlCache.keys().next().value
            const oldUrl = blobUrlCache.get(firstKey)
            if (oldUrl) {
              revokeBlobUrl(oldUrl)
            }
            blobUrlCache.delete(firstKey)
          }
          blobUrlCache.set(cacheKey, newBlobUrl)
          setBlobUrl(newBlobUrl)
          currentBlobUrlRef.current = newBlobUrl
        } else {
          setBlobUrl(null)
          currentBlobUrlRef.current = null
        }
      } else {
        setBlobUrl(null)
        currentBlobUrlRef.current = null
      }
    } catch (err) {
      if (cacheKeyRef.current === cacheKey) {
        setError(err)
        setBlobUrl(null)
        currentBlobUrlRef.current = null
      }
    } finally {
      if (cacheKeyRef.current === cacheKey) {
        setIsLoading(false)
      }
    }
  }, [dependencies, createBlobUrl, type, createCacheKey, revokeBlobUrl])

  return {
    blobUrl,
    isLoading,
    error,
    refetch
  }
}

// Utility function to clear the blob URL cache (useful for testing or memory management)
export const clearBlobUrlCache = () => {
  blobUrlCache.forEach((url) => {
    try {
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to revoke cached blob URL:', error)
    }
  })
  blobUrlCache.clear()
}